import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import {
  Avatar,
  Badge,
  Box,
  Button,
  Flex,
  type FlexProps,
  Text,
  useDisclosure,
} from '@/ui';
import type { OrgInvite } from '../hooks/useOrganizationInvites';
import { MemberPhone } from './MemberPhone';

type Props = FlexProps & {
  invite: OrgInvite;
  isRevoking?: boolean;
  isResending?: boolean;
  /** FAMILY orgs: show SMS contact details */
  smsContactsEnabled?: boolean;
  onRevoke: (inviteId: string, options?: { onSuccess?: () => void }) => void;
  onResend: (inviteId: string) => void;
};

export const PendingInviteRow = ({
  invite,
  isRevoking,
  isResending,
  smsContactsEnabled,
  onRevoke,
  onResend,
  ...flexProps
}: Props) => {
  const { t } = useTranslation('settingsModal', {
    keyPrefix: 'organizations',
  });
  const {
    isOpen: isRevokeConfirmOpen,
    onOpen: openRevokeConfirm,
    onClose: closeRevokeConfirm,
  } = useDisclosure();

  const expiresLabel = DateTime.fromISO(invite.expiresAt).toLocaleString(
    DateTime.DATE_MED
  );

  return (
    <>
      <ConfirmDialog
        confirmButtonText={t('pending_invite.revoke')}
        isLoading={isRevoking}
        isOpen={isRevokeConfirmOpen}
        title={t('pending_invite.revoke_confirm_title', {
          email: invite.email,
        })}
        onCancel={closeRevokeConfirm}
        onConfirm={() => onRevoke(invite.id, { onSuccess: closeRevokeConfirm })}
      />

      <Flex align="center" {...flexProps}>
        <Avatar mr="4" name={invite.email} size="sm" />
        <Box flex="1" lineHeight="normal" minW="0">
          <Flex align="center" gap="2">
            <Text fontWeight="medium" isTruncated>
              {invite.email}
            </Text>
            {invite.isExpired && (
              <Badge colorScheme="gray" flexShrink="0">
                {t('pending_invite.expired')}
              </Badge>
            )}
          </Flex>
          <Text color="customgray.mid" fontSize="sm">
            {invite.isExpired
              ? t('pending_invite.expired_note')
              : t('pending_invite.expires', { date: expiresLabel })}
          </Text>
        </Box>
        {/* Whether this invite will land text-ready, so a missing number is
            visible before the person joins rather than after. */}
        {smsContactsEnabled && (
          <Box mr="6">
            <MemberPhone phoneNumber={invite.phoneNumber} />
          </Box>
        )}
        <Text color="customgray.mid" fontSize="sm" mr="3">
          {t('member.role_editor')}
        </Text>
        <Button
          colorScheme="dark"
          fontSize="md"
          isDisabled={isResending}
          size="sm"
          variant="ghost"
          onClick={() => onResend(invite.id)}
        >
          {t('pending_invite.resend')}
        </Button>
        <Button
          colorScheme="dark"
          fontSize="md"
          mr="-4"
          size="sm"
          variant="ghost"
          onClick={openRevokeConfirm}
        >
          {t('pending_invite.revoke')}
        </Button>
      </Flex>
    </>
  );
};
